/**
 * Centralized logger.
 *
 * Levels:
 * - debug / info: development-only. Stripped/no-op in production builds.
 * - warn / error: emitted in all environments. `error` is routed through an
 *   optional reporter hook so we can later wire it to Sentry/Logflare/etc.
 *   without touching call sites.
 *
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   logger.debug('payload', payload);
 *   logger.error('Failed to load', err, { contactId });
 *
 * Do NOT use raw `console.*` in application code — use this module so we have
 * one place to control verbosity, redaction, and remote reporting.
 */

const isDev = import.meta.env.DEV;

type ErrorReporter = (error: unknown, context?: Record<string, unknown>) => void;

let errorReporter: ErrorReporter | null = null;

/** Register a remote error reporter (e.g., Sentry). Safe to call once at boot. */
export function setErrorReporter(reporter: ErrorReporter | null): void {
  errorReporter = reporter;
}

function emit(
  level: 'debug' | 'info' | 'warn' | 'error',
  args: unknown[]
): void {
  // eslint-disable-next-line no-console
  const fn = console[level] ?? console.log;
  // eslint-disable-next-line no-console
  fn(...args);
}

export const logger = {
  debug(...args: unknown[]): void {
    if (isDev) emit('debug', args);
  },
  info(...args: unknown[]): void {
    if (isDev) emit('info', args);
  },
  warn(...args: unknown[]): void {
    emit('warn', args);
  },
  error(...args: unknown[]): void {
    emit('error', args);
    if (errorReporter) {
      try {
        const [first, ...rest] = args;
        const ctx = rest.length > 0 ? { extra: rest } : undefined;
        errorReporter(first, ctx);
      } catch {
        // Never let the reporter throw.
      }
    }
  },
};

export default logger;
